import { IFile } from '@sdk/contracts';
import { message, Modal, Upload as BaseUpload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import axios from 'axios';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropZone, ErrorAlert, LoadingView, UploadedFileView } from './components';
import { Container } from './UploadDragAndDrop.styles';

import { useFileService } from '@/store/file/useFileService';
import { useMutateFiles } from '@/store/file/useMutateFiles';
import { FileErrorType } from '@/types/file';
import { bytesToMB, getFileDuration, getFilePagesNumber, getFilePdfWordCount } from '@/utils';

const { Dragger } = BaseUpload;

interface UploadDragAndDropProps {
  value?: string | IFile | null;
  onChange?: (value?: string | IFile | null) => void;
  acceptedTypes?: string[];
  maxSizeInMB?: number;
  disabled?: boolean;
  returnFileObject?: boolean;
  allowEdit?: boolean;
  modalConfirmOnDelete?: boolean;
}

export const UploadDragAndDrop: React.FC<UploadDragAndDropProps> = ({
  value,
  onChange,
  acceptedTypes = [],
  maxSizeInMB = 10,
  disabled = false,
  returnFileObject = false,
  allowEdit = true,
  modalConfirmOnDelete = false
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation();
  const [file, setFile] = useState<IFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<FileErrorType[]>([]);

  const { getFileWithUrl } = useFileService();
  const { uploadFile } = useMutateFiles();

  useEffect(() => {
    const loadFile = async () => {
      if (value && !file) {
        try {
          if (typeof value === 'string') {
            const loadedFile = await getFileWithUrl(value);
            if (loadedFile) setFile(loadedFile);
          } else {
            setFile(value);
          }
        } catch (err) {
          console.error('Error loading file:', err);
        }
      } else if (!value && file) {
        setFile(null);
      }
    };
    loadFile();
  }, [value, file]);
  const handleUpload = async ({ file: fileToUpload }: UploadRequestOption) => {
    const rcFile = fileToUpload as RcFile;

    if (!validateFile(rcFile)) {
      return;
    }

    try {
      setLoading(true);
      setErrors([]);
      setUploadingFileName(rcFile.name);
      setUploadProgress(0);

      const { type: mimetype, name, size } = rcFile;

      const duration = await getFileDuration(rcFile);
      const pages = await getFilePagesNumber(rcFile as File);
      const wordCount = await getFilePdfWordCount(rcFile as File);

      const newFile = await uploadFile({
        name,
        size,
        mimetype,
        duration,
        pages,
        wordCount
      });

      if (!newFile) {
        message.error(t('DefaultTranslations:UPLOAD_FAILED'));
        setLoading(false);
        return;
      }

      await axios.put(newFile.url, rcFile, {
        withCredentials: false,
        headers: { 'Content-Type': mimetype },
        onUploadProgress: progressEvent => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });

      const uploadedFile = await getFileWithUrl(newFile._id);
      if (!uploadedFile) {
        message.error(t('DefaultTranslations:UPLOAD_FAILED'));
        setLoading(false);
        return;
      }

      setFile(uploadedFile);
      onChange?.(returnFileObject ? uploadedFile : uploadedFile._id);
      setLoading(false);
    } catch (err) {
      console.error('Upload error:', err);
      message.error(t('DefaultTranslations:UPLOAD_FAILED'));
      setLoading(false);
    }
  };
  const validateFile = (rcFile: RcFile): boolean => {
    const newErrors: FileErrorType[] = [];

    if (acceptedTypes.length > 0) {
      const mimeType = rcFile.type;

      const isValidType = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          const extension = rcFile.name.split('.').pop()?.toLowerCase();
          return extension ? `.${extension}` === type.toLowerCase() : false;
        }
        if (type.endsWith('/*')) {
          return mimeType.startsWith(type.slice(0, type.length - 1));
        }
        return mimeType === type || mimeType.includes(type);
      });

      if (!isValidType) {
        newErrors.push('format');
      }
    }

    const fileSizeInMB = bytesToMB(rcFile.size);
    if (fileSizeInMB > maxSizeInMB) {
      newErrors.push('size');
    }

    if (newErrors.length > 0) {
      setErrors(prev => Array.from(new Set<FileErrorType>([...prev, ...newErrors])));
      return false;
    }

    return true;
  };

  const handleDelete = useCallback(() => {
    if (modalConfirmOnDelete && file) {
      modal.confirm({
        title: t('DefaultTranslations:DELETE_FILE'),
        content: t('DefaultTranslations:DELETE_FILE_CONFIRM_MESSAGE', { fileName: file.name }),
        okText: t('DefaultTranslations:YES_DELETE'),
        cancelText: t('DefaultTranslations:CANCEL'),
        onOk: () => {
          setFile(null);
          onChange?.(null);
          setErrors([]);
        },
        icon: null
      });
    } else {
      setFile(null);
      onChange?.(null);
      setErrors([]);
    }
  }, [file, modal, modalConfirmOnDelete, onChange, t]);

  const beforeUpload = (rcFile: RcFile, fileList: RcFile[]): boolean => {
    if (fileList.length > 1) {
      setErrors(prev => Array.from(new Set<FileErrorType>([...prev, 'multiple'])));
      return false;
    }
    return validateFile(rcFile);
  };

  const removeError = (errorType: FileErrorType) => {
    setErrors(prev => prev.filter(e => e !== errorType));
  };

  if (loading) {
    return (
      <>
        {contextHolder}
        <Container $isDragging={false}>
          <LoadingView fileName={uploadingFileName} progress={uploadProgress} />
        </Container>
      </>
    );
  }

  if (file) {
    return (
      <>
        {contextHolder}
        <Container $isDragging={false}>
          <UploadedFileView
            uploadedFile={file}
            disabled={disabled}
            acceptedTypes={acceptedTypes}
            onDelete={handleDelete}
            onUpload={handleUpload}
            beforeUpload={beforeUpload}
            allowEdit={allowEdit}
          />
        </Container>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <Container
        $isDragging={isDragging}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDrop={() => setIsDragging(false)}>
        <Dragger
          customRequest={handleUpload}
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={disabled}
          accept={acceptedTypes.join(',')}>
          <DropZone acceptedTypes={acceptedTypes} />
        </Dragger>
      </Container>
      {errors.map(errorType => (
        <ErrorAlert
          key={errorType}
          errorType={errorType}
          maxSize={maxSizeInMB}
          acceptedTypes={acceptedTypes}
          onClose={() => removeError(errorType)}
        />
      ))}
    </>
  );
};
